import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import uuidv4 from 'uuid/v4';
import {
  addFirestopItemToCreate,
  cancelEditListAttributes,
  closeAddMultipleFirestopLocationsDialog,
  closeAddNewFirestopsDialog,
  closeConfirmFirestopItemDeleteDialog,
  closeConfirmFirestopListDeleteDialog,
  closeCreateFirestopListDialog,
  closeEditFirestopItemDialog,
  closeFirestopAdditionDialog,
  closeFirestopCatalogsDialog,
  closeFirestopLabelDialog,
  closeFirestopLocationsOverviewDialog,
  closeViewAndEditFirestopListDialog,
  editFirestopAttributes,
  modifyCreateFirestopListAttribute,
  modifyFirestopAdditionAttribute,
  modifyFirestopItemToCreate,
  modifyFirestopListAttribute,
  openAddMultipleFirestopLocationsDialog,
  openAddNewFirestopDialog,
  openConfirmFirestopItemDeleteDialog,
  openConfirmFirestopListDeleteDialog,
  openCreateFirestopListDialog,
  openEditFirestopItemDialog,
  openFirestopAdditionDialog,
  openFirestopCatalogsDialog,
  openFirestopLabelDialog,
  openFirestopList,
  openFirestopLocationsOverviewDialog,
  removeFirestopImageDescription,
  removeFirestopItemToCreate,
  setFirestopImageDescription,
  setFirestopImageToAdd,
  setFirestopImageToDelete,
  setFirestopItemsSearchFilterValue,
  setFirestopLabelToDelete,
  setFirestopListsSearchFilterValue,
  setHighlightedFirestopItemId,
  setIsCreatingOrUpdatingFirestopData,
  setIsPrintingFirestopListPdf,
  toggleEditListAttributes,
} from './actions/firestop-documentation.actions';
import { createFirestopAddition } from './thunks/createFirestopAddition.thunk';
import { createFirestopLabels } from './thunks/createFirestopLabels.thunk';
import { createFirestopList } from './thunks/createFirestopList.thunk';
import { createNewFirestops } from './thunks/createNewFirestops.thunk';
import { deleteFirestopItem } from './thunks/deleteFirestopItem.thunk';
import { deleteFirestopLabel } from './thunks/deleteFirestopLabel.thunk';
import { deleteFirestopList } from './thunks/deleteFirestopList.thunk';
import { loadProjectFirestopDocumentationData } from './thunks/loadProjectFirestopDocumentationData.thunk';
import { saveCreatedAndUpdatedFirestopDetailAndProductItems } from './thunks/saveCreatedAndUpdatedFirestopDetailAndProductItems.thunk';
import { saveFirestopListChanges } from './thunks/saveFirestopListChanges.thunk';
import { updateFirestopItem } from './thunks/updateFirestopItem.thunk';

const firestopListsAdapter = createEntityAdapter();
const firestopItemsAdapter = createEntityAdapter();
const firestopImagesAdapter = createEntityAdapter();
const firestopDetailsAdapter = createEntityAdapter();
const firestopProductsAdapter = createEntityAdapter();
const firestopDetailImagesAdapter = createEntityAdapter();
const firestopLabelsAdapter = createEntityAdapter();

const firestopDocumentationSlice = createSlice({
  name: 'firestopDocumentation',
  initialState: {
    firestopLists: firestopListsAdapter.getInitialState(),
    firestopItems: firestopItemsAdapter.getInitialState(),
    firestopImages: firestopImagesAdapter.getInitialState(),
    firestopDetails: firestopDetailsAdapter.getInitialState(),
    firestopProducts: firestopProductsAdapter.getInitialState(),
    firestopDetailImages: firestopDetailImagesAdapter.getInitialState(),
    firestopLabels: firestopLabelsAdapter.getInitialState(),
    changeHistoryItemsByItemId: {},
    isCreateFirestopListDialogOpen: false,
    isAddNewFirestopDialogOpen: false,
    isAddMultipleFirestopLocationsDialogOpen: false,
    isEditingListAttributes: false,
    isSavingListAttributes: false,
    createFirestopListAttributes: {},
    loading: false,
    isCreatingOrUpdatingFirestopData: false,
    isPrintingFirestopListPdf: false,
    firestopListAttributeModifications: {},
    firestopItemsToCreate: {},
    editedFirestopAttributes: {},
    firestopImagesToAdd: [],
    firestopEditImagesToDelete: [],
    firestopLabelsToDelete: [],
    firestopAdditionAttributes: {},
    selectedFirestopListId: '',
    isConfirmDeleteFirestopListDialogOpen: false,
    isDeletingFirestopList: false,
    selectedFirestopItemIdToDelete: '',
    selectedFirestopItemIdToEdit: '',
    selectedAdditionFirestopItemId: '',
    labelDialogFirestopItemId: '',
    labelDialogListId: '',
    isCreatingFirestopLabels: false,
    imageDescriptions: {},
    latestWorkedAtByResource: {},
    isFirestopLocationsOverviewDialogOpen: false,
    isFirestopCatalogsDialogOpen: false,
    highlightedFirestopItemId: '',
    firestopListsSearchFilterValue: '',
    firestopItemsSearchFilterValue: '',
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(
        loadProjectFirestopDocumentationData.pending,
        (state, action) => {
          state.loading = true;
        }
      )
      .addCase(
        loadProjectFirestopDocumentationData.fulfilled,
        (state, action) => {
          const {
            firestopLists,
            firestopItems,
            firestopImages,
            firestopDetails,
            firestopProducts,
            firestopDetailImages,
            firestopLabels,
          } = action.payload;
          firestopListsAdapter.setMany(state.firestopLists, firestopLists);
          firestopItemsAdapter.setMany(state.firestopItems, firestopItems);
          firestopImagesAdapter.setMany(state.firestopImages, firestopImages);

          if (firestopDetails) {
            firestopDetailsAdapter.setMany(
              state.firestopDetails,
              firestopDetails
            );
          }

          if (firestopProducts) {
            firestopProductsAdapter.setMany(
              state.firestopProducts,
              firestopProducts
            );
          }

          if (firestopDetailImages) {
            firestopDetailImagesAdapter.setMany(
              state.firestopDetailImages,
              firestopDetailImages
            );
          }

          if (firestopLabels) {
            firestopLabelsAdapter.setMany(state.firestopLabels, firestopLabels);
          }

          state.loading = false;
        }
      )
      .addCase(addFirestopItemToCreate, (state, action) => {
        const newInitialFirestopItem = { id: uuidv4() };

        state.firestopItemsToCreate[newInitialFirestopItem.id] =
          newInitialFirestopItem;
      })
      .addCase(createFirestopList.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createFirestopList.fulfilled, (state, action) => {
        const { firestopList } = action.payload;
        firestopListsAdapter.setOne(state.firestopLists, firestopList);

        state.selectedFirestopListId = firestopList.id;

        state.isEditingListAttributes = false;
        state.firestopListAttributeModifications = {};

        state.isCreateFirestopListDialogOpen = false;
        state.createFirestopListAttributes = {};
        state.isCreatingOrUpdatingFirestopData = false;
      })
      .addCase(createFirestopList.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkodokumentaation luonnissa', {
          variant: 'error',
        });
      })
      .addCase(createNewFirestops.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createNewFirestops.fulfilled, (state, action) => {
        const { firestopItems, addedFirestopImages } = action.payload;
        firestopItemsAdapter.setMany(state.firestopItems, firestopItems);

        firestopImagesAdapter.setMany(
          state.firestopImages,
          addedFirestopImages
        );

        state.isCreatingOrUpdatingFirestopData = false;

        enqueueSnackbar('Palokatko(t) luotu', {
          variant: 'success',
        });

        state.firestopImagesToAdd = [];
        state.imageDescriptions = {};
      })
      .addCase(createNewFirestops.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
      })
      .addCase(openCreateFirestopListDialog, (state, action) => {
        state.isCreateFirestopListDialogOpen = true;
        state.createFirestopListAttributes = {};
      })
      .addCase(closeCreateFirestopListDialog, (state, action) => {
        state.isCreateFirestopListDialogOpen = false;
        state.createFirestopListAttributes = {};
      })
      .addCase(modifyCreateFirestopListAttribute, (state, action) => {
        state.createFirestopListAttributes = {
          ...state.createFirestopListAttributes,
          ...action.payload,
        };
      })
      .addCase(modifyFirestopItemToCreate, (state, action) => {
        const { id, attribute, value } = action.payload;

        state.firestopItemsToCreate[id] = {
          ...state.firestopItemsToCreate[id],
          [attribute]: value,
        };
      })
      .addCase(removeFirestopItemToCreate, (state, action) => {
        const id = action.payload;
        delete state.firestopItemsToCreate[id];
      })
      .addCase(editFirestopAttributes, (state, action) => {
        const { attribute, value } = action.payload;

        state.editedFirestopAttributes[attribute] = value;
      })
      .addCase(openFirestopList, (state, action) => {
        state.selectedFirestopListId = action.payload;
      })
      .addCase(closeViewAndEditFirestopListDialog, (state, action) => {
        state.selectedFirestopListId = '';
        state.firestopListAttributeModifications = {};
        state.isEditingListAttributes = false;
        state.isSavingListAttributes = false;
      })
      .addCase(modifyFirestopListAttribute, (state, action) => {
        state.firestopListAttributeModifications = {
          ...state.firestopListAttributeModifications,
          ...action.payload,
        };
      })
      .addCase(toggleEditListAttributes, (state) => {
        state.isEditingListAttributes = !state.isEditingListAttributes;
        if (!state.isEditingListAttributes) {
          state.firestopListAttributeModifications = {};
        }
      })
      .addCase(cancelEditListAttributes, (state) => {
        state.isEditingListAttributes = false;
        state.firestopListAttributeModifications = {};
      })
      .addCase(saveFirestopListChanges.pending, (state) => {
        state.isSavingListAttributes = true;
      })
      .addCase(saveFirestopListChanges.fulfilled, (state, action) => {
        const { updatedFirestopListItem } = action.payload;

        firestopListsAdapter.upsertOne(
          state.firestopLists,
          updatedFirestopListItem
        );
        state.firestopListAttributeModifications = {};
        state.isEditingListAttributes = false;
        state.isSavingListAttributes = false;

        enqueueSnackbar('Muutokset tallennettu', {
          variant: 'success',
        });
      })
      .addCase(saveFirestopListChanges.rejected, (state) => {
        state.isSavingListAttributes = false;

        enqueueSnackbar('Virhe muutosten tallennuksessa', {
          variant: 'error',
        });
      })
      .addCase(openAddNewFirestopDialog, (state, action) => {
        const id = uuidv4();
        state.isAddNewFirestopDialogOpen = true;
        state.firestopItemsToCreate[id] = { id };
      })
      .addCase(closeAddNewFirestopsDialog, (state, action) => {
        state.isAddNewFirestopDialogOpen = false;
        state.firestopItemsToCreate = {};
        state.imageDescriptions = {};
      })
      .addCase(openAddMultipleFirestopLocationsDialog, (state) => {
        // Locations are added one at a time; each confirmed location seeds its
        // own item, so we start with an empty staging set.
        state.isAddMultipleFirestopLocationsDialogOpen = true;
        state.firestopItemsToCreate = {};
      })
      .addCase(closeAddMultipleFirestopLocationsDialog, (state) => {
        state.isAddMultipleFirestopLocationsDialogOpen = false;
        state.firestopItemsToCreate = {};
        state.firestopImagesToAdd = [];
        state.imageDescriptions = {};
      })
      .addCase(openConfirmFirestopItemDeleteDialog, (state, action) => {
        state.selectedFirestopItemIdToDelete = action.payload;
      })
      .addCase(closeConfirmFirestopItemDeleteDialog, (state, action) => {
        state.selectedFirestopItemIdToDelete = '';
      })
      .addCase(deleteFirestopItem.fulfilled, (state, action) => {
        firestopItemsAdapter.removeOne(state.firestopItems, action.payload.id);
        state.selectedFirestopItemIdToDelete = '';
      })
      .addCase(openEditFirestopItemDialog, (state, action) => {
        state.selectedFirestopItemIdToEdit = action.payload;
      })
      .addCase(closeEditFirestopItemDialog, (state, action) => {
        state.selectedFirestopItemIdToEdit = '';
        state.editedFirestopAttributes = {};
        state.firestopImagesToAdd = [];
        state.firestopEditImagesToDelete = [];
        state.firestopLabelsToDelete = [];
        state.imageDescriptions = {};
      })
      .addCase(setFirestopImageToDelete, (state, action) => {
        state.firestopEditImagesToDelete.push(action.payload);
      })
      .addCase(setFirestopLabelToDelete, (state, action) => {
        state.firestopLabelsToDelete.push(action.payload);
      })
      .addCase(updateFirestopItem.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(updateFirestopItem.fulfilled, (state, action) => {
        const {
          updatedFirestopItem,
          addedFirestopImages,
          deletedFirestopImages,
          deletedFirestopLabels,
        } = action.payload;

        firestopItemsAdapter.setOne(state.firestopItems, updatedFirestopItem);

        if (addedFirestopImages && addedFirestopImages.length > 0) {
          firestopImagesAdapter.setMany(
            state.firestopImages,
            addedFirestopImages
          );
        }

        if (deletedFirestopImages && deletedFirestopImages.length > 0) {
          const removedIds = deletedFirestopImages.map(
            (firestopImage) => firestopImage.id
          );
          firestopImagesAdapter.removeMany(state.firestopImages, removedIds);
        }

        if (deletedFirestopLabels && deletedFirestopLabels.length > 0) {
          const removedLabelIds = deletedFirestopLabels.map(
            (firestopLabel) => firestopLabel.id
          );
          firestopLabelsAdapter.removeMany(
            state.firestopLabels,
            removedLabelIds
          );
        }

        state.firestopImagesToAdd = [];
        state.selectedFirestopItemIdToEdit = '';
        state.firestopEditImagesToDelete = [];
        state.firestopLabelsToDelete = [];
        state.editedFirestopAttributes = {};
        state.isCreatingOrUpdatingFirestopData = false;
        state.imageDescriptions = {};

        enqueueSnackbar('Palokatko päivitetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(updateFirestopItem.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkon päivityksessä', {
          variant: 'error',
        });
      })
      .addCase(setFirestopImageToAdd, (state, action) => {
        state.firestopImagesToAdd.push(action.payload);
      })
      .addCase(modifyFirestopAdditionAttribute, (state, action) => {
        const { attribute, value } = action.payload;

        state.firestopAdditionAttributes = {
          ...state.firestopAdditionAttributes,
          [attribute]: value,
        };
      })
      .addCase(createFirestopAddition.pending, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = true;
      })
      .addCase(createFirestopAddition.fulfilled, (state, action) => {
        const { firestopItem, addedFirestopImages } = action.payload;

        firestopItemsAdapter.setOne(state.firestopItems, firestopItem);
        firestopImagesAdapter.setMany(
          state.firestopImages,
          addedFirestopImages
        );

        state.selectedAdditionFirestopItemId = '';
        state.firestopAdditionAttributes = {};
        state.isCreatingOrUpdatingFirestopData = false;
        state.imageDescriptions = {};
        state.firestopImagesToAdd = [];

        enqueueSnackbar('Palokatko päivitetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(createFirestopAddition.rejected, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = false;
        enqueueSnackbar('Virhe palokatkon päivityksessä', {
          variant: 'error',
        });
      })
      .addCase(setIsCreatingOrUpdatingFirestopData, (state, action) => {
        state.isCreatingOrUpdatingFirestopData = action.payload;
      })
      .addCase(setFirestopImageDescription, (state, action) => {
        const { firestopItemId, description, index } = action.payload;

        const currentFirestopImageDescriptions =
          state.imageDescriptions[firestopItemId];

        state.imageDescriptions = {
          ...state.imageDescriptions,
          [firestopItemId]: {
            ...currentFirestopImageDescriptions,
            [index]: description,
          },
        };
      })
      .addCase(removeFirestopImageDescription, (state, action) => {
        const { firestopItemId, index } = action.payload;
        const currentFirestopImagesDescriptions =
          state.imageDescriptions[firestopItemId];

        if (currentFirestopImagesDescriptions) {
          delete currentFirestopImagesDescriptions[index];
        }
      })
      .addCase(openFirestopAdditionDialog, (state, action) => {
        state.selectedAdditionFirestopItemId = action.payload;
      })
      .addCase(closeFirestopAdditionDialog, (state, action) => {
        state.selectedAdditionFirestopItemId = '';
      })
      .addCase(openFirestopLabelDialog, (state, action) => {
        // Payload identifies the owner: { firestopItemId } or { listId }.
        const { firestopItemId, listId } = action.payload || {};
        state.labelDialogFirestopItemId = firestopItemId || '';
        state.labelDialogListId = listId || '';
      })
      .addCase(closeFirestopLabelDialog, (state) => {
        state.labelDialogFirestopItemId = '';
        state.labelDialogListId = '';
      })
      .addCase(createFirestopLabels.pending, (state) => {
        state.isCreatingFirestopLabels = true;
      })
      .addCase(createFirestopLabels.fulfilled, (state, action) => {
        const { firestopLabels } = action.payload || {};

        if (firestopLabels) {
          firestopLabelsAdapter.setMany(state.firestopLabels, firestopLabels);
        }

        state.isCreatingFirestopLabels = false;

        const wasListLabel = Boolean(state.labelDialogListId);

        state.labelDialogFirestopItemId = '';
        state.labelDialogListId = '';

        enqueueSnackbar(
          wasListLabel
            ? 'Listan palokatkotarra lisätty'
            : 'Palokatkon palokatkotarra lisätty',
          {
            variant: 'success',
          }
        );
      })
      .addCase(createFirestopLabels.rejected, (state) => {
        state.isCreatingFirestopLabels = false;

        enqueueSnackbar('Virhe palokatkotarran lisäyksessä', {
          variant: 'error',
        });
      })
      .addCase(deleteFirestopLabel.fulfilled, (state, action) => {
        const { labelId, deletedFirestopLabel } = action.payload || {};

        if (deletedFirestopLabel && deletedFirestopLabel.id) {
          firestopLabelsAdapter.upsertOne(
            state.firestopLabels,
            deletedFirestopLabel
          );
        } else if (labelId) {
          firestopLabelsAdapter.updateOne(state.firestopLabels, {
            id: labelId,
            changes: { deletedAt: new Date().toISOString() },
          });
        }

        enqueueSnackbar('Palokatkotarra poistettu', {
          variant: 'success',
        });
      })
      .addCase(deleteFirestopLabel.rejected, () => {
        enqueueSnackbar('Virhe palokatkotarran poistossa', {
          variant: 'error',
        });
      })
      .addCase(openFirestopCatalogsDialog, (state, action) => {
        state.isFirestopCatalogsDialogOpen = true;
      })
      .addCase(closeFirestopCatalogsDialog, (state, action) => {
        state.isFirestopCatalogsDialogOpen = false;
      })
      .addCase(
        saveCreatedAndUpdatedFirestopDetailAndProductItems.fulfilled,
        (state, action) => {
          const { firestopDetails, firestopProducts, firestopDetailImages } =
            action.payload || {};

          if (firestopDetails) {
            firestopDetailsAdapter.setMany(
              state.firestopDetails,
              firestopDetails
            );
          }

          if (firestopProducts) {
            firestopProductsAdapter.setMany(
              state.firestopProducts,
              firestopProducts
            );
          }

          if (firestopDetailImages) {
            firestopDetailImagesAdapter.setMany(
              state.firestopDetailImages,
              firestopDetailImages
            );
          }

          const createdFirestopDetailImages =
            action.meta?.arg?.createdFirestopDetailImages;
          if (
            createdFirestopDetailImages &&
            createdFirestopDetailImages.length > 0
          ) {
            firestopDetailImagesAdapter.upsertMany(
              state.firestopDetailImages,
              createdFirestopDetailImages
            );
          }

          const deletedFirestopDetailImages =
            action.meta?.arg?.deletedFirestopDetailImages;
          if (
            deletedFirestopDetailImages &&
            deletedFirestopDetailImages.length > 0
          ) {
            firestopDetailImagesAdapter.removeMany(
              state.firestopDetailImages,
              deletedFirestopDetailImages
            );
          }
        }
      )
      .addCase(setIsPrintingFirestopListPdf, (state, action) => {
        state.isPrintingFirestopListPdf = action.payload;
      })
      .addCase(deleteFirestopList.pending, (state, action) => {
        state.isDeletingFirestopList = true;
      })
      .addCase(deleteFirestopList.fulfilled, (state, action) => {
        const {
          deletedFirestopItems,
          deletedFirestopImages,
          deletedFirestopListItem,
        } = action.payload;

        if (deletedFirestopListItem) {
          firestopListsAdapter.upsertOne(
            state.firestopLists,
            deletedFirestopListItem
          );
        }

        if (deletedFirestopItems && deletedFirestopItems.length > 0) {
          firestopItemsAdapter.upsertMany(
            state.firestopItems,
            deletedFirestopItems
          );
        }

        if (deletedFirestopImages && deletedFirestopImages.length > 0) {
          firestopImagesAdapter.upsertMany(
            state.firestopImages,
            deletedFirestopImages
          );
        }

        state.isConfirmDeleteFirestopListDialogOpen = false;
        state.selectedFirestopListId = '';
        state.isDeletingFirestopList = false;

        enqueueSnackbar('Palokatkodokumentaatio poistettu onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(deleteFirestopList.rejected, (state, action) => {
        state.isDeletingFirestopList = false;
        enqueueSnackbar('Virhe palokatkodokumentaation poistossa', {
          variant: 'error',
        });
      })
      .addCase(openConfirmFirestopListDeleteDialog, (state, action) => {
        state.isConfirmDeleteFirestopListDialogOpen = true;
      })
      .addCase(closeConfirmFirestopListDeleteDialog, (state, action) => {
        state.isConfirmDeleteFirestopListDialogOpen = false;
      })
      .addCase(openFirestopLocationsOverviewDialog, (state, action) => {
        state.isFirestopLocationsOverviewDialogOpen = true;
      })
      .addCase(closeFirestopLocationsOverviewDialog, (state, action) => {
        state.isFirestopLocationsOverviewDialogOpen = false;
      })
      .addCase(setHighlightedFirestopItemId, (state, action) => {
        state.highlightedFirestopItemId = action.payload;
      })
      .addCase(setFirestopListsSearchFilterValue, (state, action) => {
        state.firestopListsSearchFilterValue = action.payload;
      })
      .addCase(setFirestopItemsSearchFilterValue, (state, action) => {
        state.firestopItemsSearchFilterValue = action.payload;
      });
  },
});

export default firestopDocumentationSlice.reducer;
